import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getQuote, placeEquityOrder } from '../api/trading'
import { previewFleet, submitFleet } from '../api/fleet'
import { ApiError } from '../api/http'
import { useAuthStore } from '../auth/useAuthStore'
import { useSetting } from '../hooks/useSetting'
import { usePortfolioSummary } from '../hooks/usePortfolioSummary'
import { useDailyCloseLimit } from '../hooks/useDailyCloseLimit'
import { useOpeningCutoff } from '../hooks/useOpeningCutoff'
import { useMaxLossLimit } from '../hooks/useMaxLossLimit'
import { useMaxGainLimit } from '../hooks/useMaxGainLimit'
import { useOrbBlock } from '../hooks/useOrbBlock'
import { useTrendBlock } from '../hooks/useTrendBlock'
import { composeBuyDisabledMessage } from '../lib/buyDisabled'
import { toast } from '../toast/useToastStore'
import { useIsMarketOpen, useSessionPhase, phaseLabel } from '../lib/marketHours'
import { planEquityOrder, wholeSharesForDollars } from '../lib/equityOrderPlan'
import { useFlatten } from '../hooks/useFlatten'
import { useLiveQuote } from '../hooks/useLiveQuote'
import { useLiveQuotesStore } from '../stores/useLiveQuotesStore'
import { PreflightGrid } from './fleet/PreflightGrid'
import { StatusGrid } from './fleet/StatusGrid'
import type {
  FleetSubmitResult,
  Order,
  OrderIntent,
  PortfolioResponse,
  QuotesResponse,
} from '../api/types'

interface Props {
  symbol: string
  /**
   * Render a slim, single-row trade bar (size input + Buy/Sell + expand) instead
   * of the full ticket. Used as the mobile collapsed state so the chart stays
   * fully visible while the common quick-trade controls remain one tap away.
   */
  collapsed?: boolean
  /** Invoked from the compact bar's expand affordance. */
  onExpand?: () => void
  /**
   * Day-trading lean layout (chart panel): drops the quote-info row, the
   * "% of equity = $" helper, and the buying-power line. The chart already
   * shows price, so these are redundant noise in a per-chart ticket.
   */
  dense?: boolean
}

const ARM_TIMEOUT_MS = 3000

type TradeMode = 'SHARES' | 'PERCENT' | 'DOLLARS'

function num(v: string | undefined | null): number | null {
  if (v == null || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

function fmtPrice(n: number | null): string {
  if (n == null) return '—'
  return `$${n.toFixed(2)}`
}

function fmtMoney(n: number): string {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

export function QuoteCard({ symbol, collapsed = false, onExpand, dense = false }: Props) {
  const accountId = useAuthStore((s) => s.accountId)
  const qc = useQueryClient()
  const marketOpen = useIsMarketOpen()
  const phase = useSessionPhase()
  const isExtended = phase === 'PRE' || phase === 'POST'

  useLiveQuote(symbol ? [symbol] : [])
  const liveStatus = useLiveQuotesStore((s) => s.status)

  const quoteQ = useQuery({
    queryKey: ['quote', symbol, accountId],
    queryFn: () => getQuote(symbol),
    enabled: !!accountId && !!symbol,
    // Stream-active: poll slowly as a safety net. Stream offline/unavailable:
    // poll at the existing 5s cadence (the per-platform /quotes route still
    // hits whichever broker owns the active account).
    refetchInterval:
      phase === 'CLOSED' ? false : marketOpen && liveStatus === 'online' ? 30_000 : 5_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    // Short staleTime so a hover-prefetched quote (usePrefetchTicker) renders
    // instantly on mount and concurrent consumers dedupe into one request.
    staleTime: 2_000,
    // Project to just the fields rendered here. The full quote carries
    // volatile fields (lastTimestamp, volume) that change every poll and
    // defeat structural sharing — without this, the whole card re-renders
    // ~once a second even when the price is unchanged.
    select: (d: QuotesResponse) => {
      const q = d.quotes?.[0]
      return q
        ? { bid: q.bid, ask: q.ask, last: q.last, securityStatus: q.securityStatus ?? null }
        : null
    },
  })
  const quote = quoteQ.data
  const bid = num(quote?.bid)
  const ask = num(quote?.ask)
  const last = num(quote?.last)
  const mid = bid != null && ask != null ? (bid + ask) / 2 : null
  const halted = (quote?.securityStatus ?? '').toLowerCase() === 'halted'

  const buyPlan = useMemo(
    () => planEquityOrder({ phase, side: 'BUY', bid, ask, last }),
    [phase, bid, ask, last],
  )
  const sellPlan = useMemo(
    () => planEquityOrder({ phase, side: 'SELL', bid, ask, last }),
    [phase, bid, ask, last],
  )

  const [defaultQuickTradeMode] = useSetting<'SHARES' | 'PERCENT' | 'DOLLARS'>(
    'default-quick-trade-mode',
    'SHARES',
  )
  const [storedMode, setStoredMode] = useSetting<TradeMode>('quick-trade-mode', defaultQuickTradeMode)
  const mode: TradeMode = storedMode
  const setModeWithStorage = useCallback(
    (next: TradeMode) => {
      setStoredMode(next)
    },
    [setStoredMode],
  )
  const [presetQty, setPresetQty] = useSetting<string>('quick-trade-qty', '1')
  const [presetPct, setPresetPct] = useSetting<string>('quick-trade-pct', '10')
  const [presetAmt, setPresetAmt] = useSetting<string>('quick-trade-amt', '100')
  const [quickEnabled] = useSetting<boolean>('quick-trade-enabled', true)
  const [armed, setArmed] = useState<'BUY' | 'SELL' | null>(null)
  const [modHeld, setModHeld] = useState(false)
  const armTimerRef = useRef<number | null>(null)

  const portfolio = usePortfolioSummary()
  const buyingPower = portfolio.bp
  const totalEquity = portfolio.portfolioValue
  const hasOpenPosition = useMemo(
    () =>
      portfolio.stockPositions.some(
        (p) => p.instrument.symbol === symbol && Math.abs(num(p.quantity) ?? 0) > 0,
      ),
    [portfolio.stockPositions, symbol],
  )

  // Fleet-mode: >= 2 armed accounts
  const accounts = useAuthStore((s) => s.accounts)
  const armedCount = useMemo(() => accounts.filter(a => a.armed).length, [accounts])
  const isFleetMode = armedCount >= 2

  // Reference price for preflight / fleet submit
  const referencePrice = last ?? mid ?? 0

  const [lastFleetResult, setLastFleetResult] = useState<FleetSubmitResult | null>(null)

  // Resolve sizing into wire shape
  const fleetSizing = useMemo(() => {
    if (mode === 'SHARES') return { mode: 'SHARES' as const, value: parseFloat(presetQty) || 0 }
    if (mode === 'PERCENT') return { mode: 'PERCENT' as const, value: parseFloat(presetPct) || 0 }
    return { mode: 'DOLLARS' as const, value: parseFloat(presetAmt) || 0 }
  }, [mode, presetQty, presetPct, presetAmt])

  // Preview payload — side is BUY for preflight (sizing is side-neutral); submit swaps it.
  const previewPayload = useMemo(() => {
    const intent: OrderIntent = {
      symbol,
      instrumentType: 'EQUITY' as const,
      side: 'BUY' as const,
      type: buyPlan?.mode ?? 'MARKET',
    }
    if (buyPlan?.mode === 'LIMIT') {
      intent.limitPrice = buyPlan.limitPrice
      intent.extendedHours = buyPlan.extendedHours
    }
    return { intent, sizing: fleetSizing, referencePrice }
  }, [symbol, buyPlan, fleetSizing, referencePrice])

  const preflightQuery = useQuery({
    queryKey: ['fleet', 'preflight', previewPayload],
    queryFn: () => previewFleet(previewPayload),
    enabled: isFleetMode && referencePrice > 0 && fleetSizing.value > 0,
    staleTime: 500,
    placeholderData: keepPreviousData,
  })

  // Auto-dismiss StatusGrid when the user changes symbol/sizing (moving on to a new trade)
  useEffect(() => { setLastFleetResult(null) }, [previewPayload])

  const fleetMutation = useMutation({
    mutationFn: (side: 'BUY' | 'SELL') => {
      const plan = planEquityOrder({ phase, side, bid, ask, last })
      if (plan == null) throw new Error('No live price for extended-hours limit order')
      const intent: OrderIntent = {
        ...previewPayload.intent,
        side,
        type: plan.mode,
        limitPrice: plan.limitPrice,
        ...(plan.extendedHours ? { extendedHours: true } : {}),
      }
      return submitFleet({ ...previewPayload, intent })
    },
    onSuccess: (result: FleetSubmitResult) => {
      setLastFleetResult(result)
      qc.invalidateQueries({ queryKey: ['portfolio'] })
      qc.invalidateQueries({ queryKey: ['app-orders'] })
      qc.invalidateQueries({ queryKey: ['ticker-fills'] })
    },
    onError: (err) => {
      const msg =
        err instanceof ApiError
          ? `${err.message}: ${err.body || 'no detail'}`
          : err instanceof Error
            ? err.message
            : 'Unknown error'
      toast.error(`Fleet order failed — ${msg}`)
    },
  })

  // Fleet dispatch guard: are any rows dispatchable?
  const fleetCanDispatch = isFleetMode
    ? (preflightQuery.data?.rows.filter(r => r.status !== 'skip').length ?? 0) > 0
    : true

  const inputValue = mode === 'SHARES' ? presetQty : mode === 'PERCENT' ? presetPct : presetAmt
  const setInputValue = (v: string) => {
    if (mode === 'SHARES') setPresetQty(v)
    else if (mode === 'PERCENT') setPresetPct(v)
    else setPresetAmt(v)
  }

  const parsedInput = Number(inputValue)
  const inputValid = Number.isFinite(parsedInput) && parsedInput > 0
  const resolvedDollars =
    mode === 'DOLLARS'
      ? inputValid
        ? parsedInput
        : null
      : mode === 'PERCENT'
        ? inputValid && totalEquity > 0
          ? (parsedInput / 100) * totalEquity
          : null
        : null
  const resolvedShares =
    mode === 'SHARES' && inputValid && Number.isInteger(parsedInput) ? parsedInput : null
  const exceedsBuyingPower =
    resolvedDollars != null && buyingPower > 0 && resolvedDollars > buyingPower

  const mutation = useMutation({
    mutationFn: (params: {
      side: 'BUY' | 'SELL'
      quantity?: number
      amount?: number
      mode: 'MARKET' | 'LIMIT'
      limitPrice?: number
      extendedHours?: boolean
    }) =>
      placeEquityOrder({
        symbol,
        side: params.side,
        quantity: params.quantity,
        amount: params.amount,
        mode: params.mode,
        limitPrice: params.limitPrice,
        extendedHours: params.extendedHours,
      }),
    // Optimistically surface the order as pending so TickerPendingOrders /
    // rails render it immediately instead of waiting for the broker round
    // trip + portfolio refetch. Rolled back on error; the real orderId is
    // patched in on success so cancel works before the next refetch lands.
    onMutate: async (params) => {
      const portfolioKey = ['portfolio', accountId]
      await qc.cancelQueries({ queryKey: portfolioKey })
      const previous = qc.getQueryData<PortfolioResponse>(portfolioKey)
      const optimisticId = `optimistic-${Date.now()}-${Math.random().toString(36).slice(2)}`
      if (previous) {
        const synthetic: Order = {
          orderId: optimisticId,
          instrument: { symbol, type: 'EQUITY' },
          createdAt: new Date().toISOString(),
          type: params.mode,
          side: params.side,
          status: 'PENDING_SUBMIT',
          ...(params.quantity != null
            ? { quantity: String(params.quantity) }
            : { notionalValue: String(params.amount) }),
          ...(params.limitPrice != null ? { limitPrice: String(params.limitPrice) } : {}),
        }
        qc.setQueryData<PortfolioResponse>(portfolioKey, {
          ...previous,
          orders: [synthetic, ...previous.orders],
        })
      }
      return { previous, optimisticId }
    },
    onSuccess: (resp, params, ctx) => {
      if (ctx?.optimisticId) {
        qc.setQueryData<PortfolioResponse>(['portfolio', accountId], (cur) =>
          cur
            ? {
                ...cur,
                orders: cur.orders.map((o) =>
                  o.orderId === ctx.optimisticId
                    ? { ...o, orderId: resp.orderId, status: resp.status ?? o.status }
                    : o,
                ),
              }
            : cur,
        )
      }
      const verb = params.side === 'BUY' ? 'Bought' : 'Sold'
      const detail =
        params.quantity != null
          ? `${params.quantity} ${symbol}`
          : `${fmtMoney(params.amount!)} of ${symbol}`
      const priceTag =
        params.mode === 'LIMIT' && params.limitPrice != null
          ? ` @ ${fmtPrice(params.limitPrice)}`
          : ''
      toast.success(`${verb} ${detail}${priceTag} · Order ${resp.orderId}`)
      qc.invalidateQueries({ queryKey: ['portfolio'] })
      qc.invalidateQueries({ queryKey: ['app-orders'] })
      qc.invalidateQueries({ queryKey: ['ticker-fills'] })
    },
    onError: (err, params, ctx) => {
      if (ctx?.previous) qc.setQueryData(['portfolio', accountId], ctx.previous)
      const msg =
        err instanceof ApiError
          ? `${err.message}: ${err.body || 'no detail'}`
          : err instanceof Error
            ? err.message
            : 'Unknown error'
      const detail =
        params.quantity != null
          ? `${params.quantity} ${symbol}`
          : `${fmtMoney(params.amount!)} of ${symbol}`
      toast.error(`${params.side} ${detail} failed — ${msg}`)
    },
  })

  function clearArm() {
    if (armTimerRef.current != null) {
      window.clearTimeout(armTimerRef.current)
      armTimerRef.current = null
    }
    setArmed(null)
  }

  const flattenHook = useFlatten({ symbol })
  const [flattenArmed, setFlattenArmed] = useState(false)
  const flattenArmTimerRef = useRef<number | null>(null)
  function clearFlattenArm() {
    if (flattenArmTimerRef.current != null) {
      window.clearTimeout(flattenArmTimerRef.current)
      flattenArmTimerRef.current = null
    }
    setFlattenArmed(false)
  }
  function handleFlattenClick() {
    clearArm()
    if (flattenHook.isPending) return
    // Quick-trade skips the confirm step; otherwise Flatten is two-click since it's destructive.
    if (quickEnabled) {
      clearFlattenArm()
      flattenHook.flatten()
      return
    }
    if (!flattenArmed) {
      setFlattenArmed(true)
      if (flattenArmTimerRef.current != null) window.clearTimeout(flattenArmTimerRef.current)
      flattenArmTimerRef.current = window.setTimeout(() => {
        setFlattenArmed(false)
        flattenArmTimerRef.current = null
      }, ARM_TIMEOUT_MS)
      return
    }
    clearFlattenArm()
    flattenHook.flatten()
  }

  useEffect(() => {
    clearArm()
    clearFlattenArm()
  }, [symbol])

  useEffect(() => () => clearArm(), [])

  const submitting = mutation.isPending || fleetMutation.isPending
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.metaKey || e.ctrlKey) setModHeld(true)
      if (!(e.metaKey || e.ctrlKey)) return
      if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return
      if (e.repeat) return
      if (submitting) return
      e.preventDefault()
      handleClick(e.key === 'ArrowUp' ? 'BUY' : 'SELL')
    }
    function onKeyUp(e: KeyboardEvent) {
      if (e.key === 'Meta' || e.key === 'Control') setModHeld(false)
    }
    function reset() {
      setModHeld(false)
    }
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', onKeyUp)
    window.addEventListener('blur', reset)
    document.addEventListener('visibilitychange', reset)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('keyup', onKeyUp)
      window.removeEventListener('blur', reset)
      document.removeEventListener('visibilitychange', reset)
    }
  }, [mode, inputValue, submitting, symbol, isFleetMode, phase])

  function submit(side: 'BUY' | 'SELL') {
    if (side === 'BUY' && buyBlockedMessage) {
      toast.error(`BUY ${symbol} blocked — ${buyBlockedMessage}`)
      return
    }
    if (side === 'SELL' && !hasOpenPosition) {
      toast.error(`SELL ${symbol} blocked — no open position`)
      return
    }
    if (!inputValid) {
      toast.error(`${side} ${symbol} failed — enter a value > 0`)
      return
    }
    const plan = planEquityOrder({ phase, side, bid, ask, last })
    if (plan == null) {
      toast.error(`${side} ${symbol} failed — no live price for extended-hours limit order`)
      return
    }
    const common = { mode: plan.mode, limitPrice: plan.limitPrice, extendedHours: plan.extendedHours }
    if (mode === 'SHARES') {
      if (resolvedShares == null) {
        toast.error(`${side} ${symbol} failed — whole-share qty > 0`)
        return
      }
      clearArm()
      mutation.mutate({ side, quantity: resolvedShares, ...common })
      return
    }
    if (resolvedDollars == null || resolvedDollars <= 0) {
      toast.error(
        `${side} ${symbol} failed — ${
          mode === 'PERCENT' ? 'need a positive % and account equity' : 'need a positive $ amount'
        }`,
      )
      return
    }
    if (side === 'BUY' && buyingPower > 0 && resolvedDollars > buyingPower) {
      toast.error(
        `BUY ${symbol} failed — ${fmtMoney(resolvedDollars)} exceeds buying power ${fmtMoney(buyingPower)}`,
      )
      return
    }
    clearArm()
    if (plan.extendedHours) {
      // Extended hours = whole shares only on every platform.
      const shares = wholeSharesForDollars(resolvedDollars, plan.limitPrice!)
      if (shares < 1) {
        toast.error(`${side} ${symbol} failed — ${fmtMoney(resolvedDollars)} is below one share (${fmtPrice(plan.limitPrice!)})`)
        return
      }
      mutation.mutate({ side, quantity: shares, ...common })
      return
    }
    mutation.mutate({
      side,
      amount: Number(resolvedDollars.toFixed(2)),
      ...common,
    })
  }

  function handleClick(side: 'BUY' | 'SELL') {
    // Fleet path: skip arm/confirm, fire immediately
    if (isFleetMode) {
      fleetMutation.mutate(side)
      return
    }
    // Single-account path — unchanged
    if (quickEnabled) {
      submit(side)
      return
    }
    if (armed === side) {
      submit(side)
      return
    }
    setArmed(side)
    if (armTimerRef.current != null) window.clearTimeout(armTimerRef.current)
    armTimerRef.current = window.setTimeout(() => {
      setArmed(null)
      armTimerRef.current = null
    }, ARM_TIMEOUT_MS)
  }

  function fleetButtonLabel(side: 'BUY' | 'SELL'): string {
    const okRows = preflightQuery.data?.rows.filter(r => r.status !== 'skip') ?? []
    const totalCost = okRows.reduce((s, r) => s + r.estCost, 0)
    if (fleetMutation.isPending) {
      return `${side === 'BUY' ? 'Buying' : 'Selling'} on ${okRows.length} accounts…`
    }
    if (okRows.length === 0) return 'No accounts can execute'
    return `${side === 'BUY' ? 'Buy' : 'Sell'} on ${okRows.length} of ${preflightQuery.data?.armedCount ?? armedCount} · ~$${totalCost.toFixed(0)}`
  }

  function orderSummary(side: 'BUY' | 'SELL'): string {
    if (mode === 'SHARES') return resolvedShares != null ? String(resolvedShares) : (inputValue || '?')
    const planForSide = side === 'BUY' ? buyPlan : sellPlan
    if (isExtended && resolvedDollars != null && planForSide?.limitPrice != null) {
      const shares = wholeSharesForDollars(resolvedDollars, planForSide.limitPrice)
      return `${shares} sh (${fmtMoney(resolvedDollars)})`
    }
    if (mode === 'DOLLARS') return resolvedDollars != null ? fmtMoney(resolvedDollars) : '?'
    return resolvedDollars != null
      ? `${inputValue}% (${fmtMoney(resolvedDollars)})`
      : `${inputValue || '?'}%`
  }

  function limitPreviewFor(side: 'BUY' | 'SELL'): number | null {
    const plan = side === 'BUY' ? buyPlan : sellPlan
    return plan?.mode === 'LIMIT' ? plan.limitPrice ?? null : null
  }

  function buttonLabel(side: 'BUY' | 'SELL') {
    if (submitting && mutation.variables?.side === side) {
      const v = mutation.variables
      const detail = v.quantity != null ? String(v.quantity) : fmtMoney(v.amount!)
      const priceTag =
        v.mode === 'LIMIT' && v.limitPrice != null ? ` @ ${fmtPrice(v.limitPrice)}` : ''
      return `${side === 'BUY' ? 'Buying' : 'Selling'} ${detail}${priceTag}…`
    }
    const verb = side === 'BUY' ? 'Buy' : 'Sell'
    const summary = orderSummary(side)
    const preview = limitPreviewFor(side)
    const priceTag = preview != null ? ` @ ${fmtPrice(preview)}` : isExtended ? ' @ —' : ''
    // In SHARES mode the dollar amount isn't already part of the summary, so
    // surface a notional estimate. Use side-specific reference (ask for buy,
    // bid for sell) so the figure reflects a realistic market fill.
    let estimateTag = ''
    if (mode === 'SHARES' && resolvedShares != null && resolvedShares > 0) {
      const refPrice =
        preview ??
        (side === 'BUY' ? (ask ?? last ?? mid) : (bid ?? last ?? mid))
      if (refPrice != null) {
        estimateTag = ` · ~${fmtMoney(resolvedShares * refPrice)}`
      }
    }
    if (armed === side) return `Confirm ${verb} ${summary}${priceTag}${estimateTag}`
    return `${verb} ${summary}${priceTag}${estimateTag}`
  }

  const closeLimit = useDailyCloseLimit()
  const openingCutoff = useOpeningCutoff()
  const maxLoss = useMaxLossLimit()
  const maxGain = useMaxGainLimit()
  const orbBlock = useOrbBlock(symbol, last)
  const trendBlock = useTrendBlock(symbol)
  const buyBlockedMessage = composeBuyDisabledMessage([
    openingCutoff.reason,
    closeLimit.capReason,
    maxLoss.capReason,
    maxGain.capReason,
    orbBlock.reason,
    trendBlock.longReason,
  ])
  const extendedTooSmall =
    isExtended &&
    mode !== 'SHARES' &&
    resolvedDollars != null &&
    buyPlan?.limitPrice != null &&
    wholeSharesForDollars(resolvedDollars, buyPlan.limitPrice) < 1
  const buyDisabled =
    submitting ||
    !inputValid ||
    (mode !== 'SHARES' && exceedsBuyingPower) ||
    buyPlan == null ||
    extendedTooSmall ||
    buyBlockedMessage != null ||
    maxLoss.capReached ||
    maxGain.capReached
  const sellDisabled =
    submitting ||
    !inputValid ||
    sellPlan == null ||
    maxLoss.capReached ||
    !hasOpenPosition

  if (!accountId) return null

  if (collapsed) {
    const cycleMode = () => {
      const next: TradeMode = mode === 'SHARES' ? 'PERCENT' : mode === 'PERCENT' ? 'DOLLARS' : 'SHARES'
      setModeWithStorage(next)
      clearArm()
    }
    const modeGlyph = mode === 'SHARES' ? 'sh' : mode === 'PERCENT' ? '%' : '$'
    const buyText = armed === 'BUY' ? 'Confirm' : isFleetMode ? 'Buy' : `Buy ${orderSummary('BUY')}`
    const sellText = armed === 'SELL' ? 'Confirm' : isFleetMode ? 'Sell' : `Sell ${orderSummary('SELL')}`
    return (
      <div className="flex items-stretch gap-1.5 px-2 py-2">
        <button
          type="button"
          onClick={cycleMode}
          disabled={submitting}
          aria-label={`Size mode: ${modeGlyph}. Tap to change.`}
          className="shrink-0 w-9 rounded border border-zinc-700 bg-zinc-950 font-mono text-xs text-zinc-300 hover:text-zinc-100 hover:bg-zinc-900 disabled:opacity-50"
        >
          {modeGlyph}
        </button>
        <input
          type="number"
          min={mode === 'SHARES' ? 1 : 0}
          step={mode === 'SHARES' ? 1 : mode === 'PERCENT' ? 1 : 0.01}
          aria-label={mode === 'SHARES' ? 'Quantity' : mode === 'PERCENT' ? 'Percent of equity' : 'Dollar amount'}
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value)
            clearArm()
          }}
          disabled={submitting}
          className="shrink-0 w-14 bg-zinc-950 border border-zinc-700 rounded px-1 text-zinc-100 font-mono text-sm text-center focus:outline-none focus:border-emerald-500 disabled:opacity-50"
        />
        <button
          type="button"
          onClick={() => handleClick('BUY')}
          disabled={isFleetMode ? submitting || !fleetCanDispatch : buyDisabled}
          className={`flex-1 min-w-0 truncate rounded border-2 px-2 py-2 text-sm font-semibold transition ${
            armed === 'BUY'
              ? 'bg-emerald-600 border-emerald-400 text-white'
              : 'bg-emerald-950/40 border-emerald-800 text-emerald-300 active:bg-emerald-900/60'
          } disabled:opacity-40 disabled:cursor-not-allowed`}
        >
          ↑ {buyText}
        </button>
        <button
          type="button"
          onClick={() => handleClick('SELL')}
          disabled={isFleetMode ? submitting || !fleetCanDispatch : sellDisabled}
          className={`flex-1 min-w-0 truncate rounded border-2 px-2 py-2 text-sm font-semibold transition ${
            armed === 'SELL'
              ? 'bg-red-600 border-red-400 text-white'
              : 'bg-red-950/40 border-red-800 text-red-300 active:bg-red-900/60'
          } disabled:opacity-40 disabled:cursor-not-allowed`}
        >
          ↓ {sellText}
        </button>
        <button
          type="button"
          onClick={onExpand}
          aria-label="Expand trade panel"
          className="shrink-0 w-8 grid place-items-center rounded text-zinc-400 hover:text-zinc-100 hover:bg-white/5"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <polyline points="18 15 12 9 6 15" />
          </svg>
        </button>
      </div>
    )
  }

  return (
    <div className={dense ? 'flex h-full flex-col' : undefined}>
      <div className={`flex flex-wrap items-center border-b border-zinc-800 ${dense ? 'shrink-0 gap-2 px-2 py-2' : 'gap-2 sm:gap-3 px-3 py-2 sm:p-4'}`}>
        <div
          role="radiogroup"
          aria-label="Order size mode"
          className="inline-flex rounded border border-zinc-700 overflow-hidden text-sm"
        >
          {(['SHARES', 'PERCENT', 'DOLLARS'] as const).map((m) => {
            const label = m === 'SHARES' ? 'Shares' : m === 'PERCENT' ? '%' : '$'
            const selected = mode === m
            return (
              <button
                key={m}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => {
                  setModeWithStorage(m)
                  clearArm()
                }}
                disabled={submitting}
                className={`px-3 py-1.5 font-mono leading-5 transition ${
                  selected
                    ? 'bg-emerald-600 text-white'
                    : 'bg-zinc-950 text-zinc-400 hover:bg-zinc-900 hover:text-zinc-200'
                } disabled:opacity-50`}
              >
                {label}
              </button>
            )
          })}
        </div>
        <label className="flex items-center gap-2 text-xs text-zinc-400">
          <input
            type="number"
            min={mode === 'SHARES' ? 1 : 0}
            step={mode === 'SHARES' ? 1 : mode === 'PERCENT' ? 1 : 0.01}
            aria-label={
              mode === 'SHARES' ? 'Quantity' : mode === 'PERCENT' ? 'Percent of equity' : 'Dollar amount'
            }
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value)
              clearArm()
            }}
            disabled={submitting}
            className="w-24 bg-zinc-950 border border-zinc-700 rounded px-2 py-1.5 text-zinc-100 font-mono text-sm text-center focus:outline-none focus:border-emerald-500 disabled:opacity-50"
          />
          {mode === 'PERCENT' && <span className="font-mono text-zinc-500">%</span>}
          {mode === 'DOLLARS' && <span className="font-mono text-zinc-500">USD</span>}
        </label>
        {mode === 'PERCENT' && !dense && (
          <div className="text-xs text-zinc-500">
            of equity{' '}
            <span className="font-mono text-zinc-300">{fmtMoney(totalEquity)}</span>
            {resolvedDollars != null && (
              <>
                {' '}
                ={' '}
                <span
                  className={`font-mono ${exceedsBuyingPower ? 'text-red-400' : 'text-zinc-300'}`}
                >
                  {fmtMoney(resolvedDollars)}
                </span>
              </>
            )}
          </div>
        )}
        {mode === 'DOLLARS' && resolvedDollars != null && exceedsBuyingPower && (
          <div className="text-xs text-red-400">
            Exceeds buying power{' '}
            <span className="font-mono">{fmtMoney(buyingPower)}</span>
          </div>
        )}
        {mode !== 'SHARES' && !exceedsBuyingPower && buyingPower > 0 && !dense && (
          <div className="text-xs text-zinc-500">
            BP <span className="font-mono text-zinc-300">{fmtMoney(buyingPower)}</span>
          </div>
        )}
      </div>

      <div className={dense ? 'flex min-h-0 flex-1 flex-col' : 'p-3 sm:p-6 space-y-2 sm:space-y-4'}>
        {!dense && (
        <div className="text-center text-xs sm:text-sm text-zinc-400">
          <span className="text-zinc-200 font-semibold">{symbol}</span>
          <span className="mx-1 sm:mx-2 text-zinc-600">·</span>
          <span className="font-mono">{fmtPrice(last)}</span>
          <span className="mx-1 sm:mx-2 text-zinc-600">·</span>
          <span>
            bid <span className="font-mono text-zinc-300">{fmtPrice(bid)}</span>
          </span>
          <span className="mx-1 sm:mx-2 text-zinc-600">·</span>
          <span>
            ask <span className="font-mono text-zinc-300">{fmtPrice(ask)}</span>
          </span>
          <span className="mx-1 sm:mx-2 text-zinc-600">·</span>
          <span>
            mid <span className="font-mono text-zinc-300">{fmtPrice(mid)}</span>
          </span>
          <span className="mx-1 sm:mx-2 text-zinc-600">·</span>
          <span
            className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${
              phase === 'RTH'
                ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30'
                : phase === 'CLOSED'
                  ? 'bg-zinc-500/10 text-zinc-400 border border-zinc-600/40'
                  : 'bg-amber-500/10 text-amber-400 border border-amber-500/30'
            }`}
          >
            {phaseLabel(phase)}
          </span>
          {halted && (
            <>
              <span className="mx-1 sm:mx-2 text-zinc-600">·</span>
              <span
                title="Trading halted"
                className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-rose-500/15 text-rose-300 border border-rose-500/30"
              >
                Halted
              </span>
            </>
          )}
        </div>
        )}

        <div className={dense ? 'flex min-h-0 flex-1 flex-col' : 'grid grid-cols-2 gap-2 sm:gap-4'}>
          <button
            type="button"
            onClick={() => handleClick('BUY')}
            disabled={isFleetMode ? (submitting || !fleetCanDispatch) : buyDisabled}
            title={
              isFleetMode
                ? (!fleetCanDispatch ? 'No accounts can execute this order' : undefined)
                : (buyBlockedMessage ??
                  (mode !== 'SHARES' && exceedsBuyingPower
                    ? `Exceeds buying power ${fmtMoney(buyingPower)}`
                    : undefined))
            }
            className={`relative flex flex-col items-center justify-center gap-1 font-semibold transition ${
              dense ? 'min-h-0 flex-1 py-2.5' : 'rounded-lg border-2 py-3 sm:py-8'
            } ${
              armed === 'BUY'
                ? 'bg-emerald-600 border-emerald-400 text-white'
                : 'bg-emerald-950/40 border-emerald-800 text-emerald-300 hover:bg-emerald-900/50 hover:border-emerald-600'
            } disabled:opacity-40 disabled:cursor-not-allowed`}
          >
            <span className={`leading-none ${dense ? 'text-2xl' : 'text-xl sm:text-3xl'}`}>↑</span>
            <span className={dense ? 'text-sm' : 'text-base'}>{isFleetMode ? fleetButtonLabel('BUY') : buttonLabel('BUY')}</span>
            <kbd
              aria-hidden
              className={`absolute top-2 right-2 px-1.5 py-0.5 text-[10px] font-mono rounded border border-emerald-700/60 bg-emerald-950/80 text-emerald-200 transition-opacity duration-150 ${
                modHeld && !isFleetMode ? 'opacity-100' : 'opacity-0'
              }`}
            >
              ⌘↑
            </kbd>
          </button>
          <button
            type="button"
            onClick={() => handleClick('SELL')}
            disabled={isFleetMode ? (submitting || !fleetCanDispatch) : sellDisabled}
            title={
              isFleetMode
                ? (!fleetCanDispatch ? 'No accounts can execute this order' : undefined)
                : (!hasOpenPosition ? `No open position in ${symbol}` : undefined)
            }
            className={`relative flex flex-col items-center justify-center gap-1 font-semibold transition ${
              dense ? 'min-h-0 flex-1 py-2.5' : 'rounded-lg border-2 py-3 sm:py-8'
            } ${
              armed === 'SELL'
                ? 'bg-red-600 border-red-400 text-white'
                : 'bg-red-950/40 border-red-800 text-red-300 hover:bg-red-900/50 hover:border-red-600'
            } disabled:opacity-40 disabled:cursor-not-allowed`}
          >
            <span className={`leading-none ${dense ? 'text-2xl' : 'text-xl sm:text-3xl'}`}>↓</span>
            <span className={dense ? 'text-sm' : 'text-base'}>{isFleetMode ? fleetButtonLabel('SELL') : buttonLabel('SELL')}</span>
            <kbd
              aria-hidden
              className={`absolute top-2 right-2 px-1.5 py-0.5 text-[10px] font-mono rounded border border-red-700/60 bg-red-950/80 text-red-200 transition-opacity duration-150 ${
                modHeld && !isFleetMode ? 'opacity-100' : 'opacity-0'
              }`}
            >
              ⌘↓
            </kbd>
          </button>
        </div>

        {hasOpenPosition && !isFleetMode && (
          <button
            type="button"
            onClick={handleFlattenClick}
            disabled={flattenHook.isPending}
            className={`w-full text-xs font-semibold py-2 rounded transition disabled:opacity-40 disabled:cursor-not-allowed ${
              flattenArmed
                ? 'bg-red-500 text-white ring-2 ring-red-300 animate-pulse'
                : 'bg-zinc-900 hover:bg-red-950/60 text-red-300 border border-red-900'
            }`}
          >
            {flattenHook.isPending
              ? (flattenHook.progress ?? 'Flattening…')
              : flattenArmed
                ? 'Confirm flatten — cancel orders & sell all'
                : 'Flatten position'}
          </button>
        )}

        {isFleetMode && (
          lastFleetResult ? (
            <div className="mt-4 border-t border-zinc-800 -mx-6 p-3">
              <StatusGrid
                result={lastFleetResult}
                accounts={accounts}
                onDismiss={() => setLastFleetResult(null)}
              />
            </div>
          ) : preflightQuery.data ? (
            <div className="mt-4 border-t border-zinc-800 -mx-6">
              <div className="px-4 py-2 flex items-center justify-between">
                <div className="text-[11px] text-zinc-500 uppercase tracking-wider">Fleet preflight</div>
                <div className="text-[11px] font-mono text-emerald-400">
                  {preflightQuery.data.rows.filter(r => r.status !== 'skip').length} of {preflightQuery.data.armedCount} will execute
                </div>
              </div>
              <PreflightGrid
                rows={preflightQuery.data.rows}
                accounts={accounts}
                referencePrice={referencePrice}
                variant="full"
              />
            </div>
          ) : null
        )}

        {!quickEnabled && armed && (
          <div className="mt-3 text-center text-xs text-zinc-500">
            Click again within {ARM_TIMEOUT_MS / 1000}s to confirm, or wait to cancel.
          </div>
        )}

        {buyBlockedMessage && (
          <div className="mt-3 text-center text-xs text-amber-400">{buyBlockedMessage}</div>
        )}

        {maxLoss.capReached && (
          <div className="mt-3 text-center text-xs text-red-400 font-semibold">
            {maxLoss.capReason} — no more orders until tomorrow.
          </div>
        )}

        {maxGain.capReached && !maxLoss.capReached && (
          <div className="mt-3 text-center text-xs text-emerald-400 font-semibold">
            {maxGain.capReason} — no new opens until tomorrow.
          </div>
        )}

      </div>
    </div>
  )
}
